// AUTO-GENERATED FILE - DO NOT EDIT
// Generated by scripts/generate-cube-meta.ts
import type {
  CubeMeasureEntry,
  CubeDimensionEntry,
  CubeJoinEntry
} from 'shared/src/api/cube-api-types';

export const lineItemsCubeMeta = {
  name: 'line_items',
  sqlTable: 'metrics.line_items',
  title: 'Line Items',
  description:
    "Line-item-level aggregates. One row per line item. To group any measure by the individually assigned media trader, group by line_item_media_traders.media_trader_name; to filter to one trader, use a 'contains' filter on media_trader_ids. Never group by media_traders_json / media_trader_ids — they are roster arrays, so grouping buckets by roster, not by trader.",
  joins: {
    line_item_metrics_daily: { relationship: 'one_to_many' } as const satisfies CubeJoinEntry,
    campaigns: { relationship: 'many_to_one' } as const satisfies CubeJoinEntry
  },
  measures: {
    'line_items.count': {
      type: 'number',
      aggType: 'count',
      title: 'Line Item Count',
      description: 'Number of line items. Can aggregate across entities.',
      meta: {
        category: 'utility',
        grain: 'line_item',
        supports_rollup: true,
        formula: 'COUNT(*)',
        inputs: [],
        short_description: 'Number of line items',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.price': {
      sql: 'price',
      type: 'number',
      aggType: 'sum',
      title: 'Price',
      format: 'currency',
      description: 'Total price of line items. Can aggregate across entities.',
      meta: {
        category: 'revenue',
        grain: 'line_item',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(price)',
        inputs: [],
        short_description: 'Total contract price of line items (gross, before referral commission)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.effective_price': {
      sql: 'line_items.price * (1 - COALESCE(line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Effective Price',
      format: 'currency',
      description:
        'Net price after deducting referral commission. This is the revenue the agency retains after paying referral partners. When referral_commission_rate is 0 (no referral partner), effective_price equals price. Used as the denominator for percent_revenue_delivered and the baseline for revenue_remaining.',
      meta: {
        category: 'revenue',
        grain: 'line_item',
        type: 'effective',
        supports_rollup: true,
        formula: 'price * (1 - referral_commission_rate)',
        inputs: ['line_items.price', 'line_items.referral_commission_rate'],
        short_description:
          'Net contract price after deducting referral commission (revenue the agency retains)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.effective_unit_price': {
      sql: 'line_items.unit_price * (1 - COALESCE(line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'avg',
      title: 'Effective Unit Price',
      format: 'currency',
      description:
        'Net unit price after deducting referral commission. The per-unit rate retained after referral payouts. Uses weighted average across entities.',
      meta: {
        category: 'cost',
        grain: 'line_item',
        type: 'effective',
        supports_rollup: true,
        formula: 'unit_price * (1 - referral_commission_rate)',
        inputs: ['line_items.unit_price', 'line_items.referral_commission_rate'],
        short_description: 'Net per-unit price after referral commission',
        source_tables: ['metrics.line_items'],
        is_unit_rate: true
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.gross_price': {
      sql: 'line_items.price / NULLIF(1 - line_items.agency_markup_rate, 0)',
      type: 'number',
      aggType: 'sum',
      title: 'Gross Price',
      format: 'currency',
      description:
        'Client-facing price including agency markup. The total amount billed to client. Can aggregate across entities.',
      meta: {
        category: 'gross',
        grain: 'line_item',
        supports_rollup: true,
        formula: 'price / (1 - agency_markup_rate)',
        inputs: ['line_items.price', 'line_items.agency_markup_rate'],
        short_description: 'Client-facing price including agency markup (total billed to client)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.media_budget': {
      sql: 'media_budget',
      type: 'number',
      aggType: 'sum',
      title: 'Media Budget',
      format: 'currency',
      description: 'Media budget allocated to line items. Can aggregate across entities.',
      meta: {
        category: 'spend',
        grain: 'line_item',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(media_budget)',
        inputs: [],
        short_description: 'Media budget allocated to line items',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.estimated_units': {
      sql: 'estimated_units',
      type: 'number',
      aggType: 'sum',
      title: 'Estimated Units',
      format: 'number',
      description: 'Total units to deliver across all line items. Can aggregate across entities.',
      meta: {
        category: 'target',
        grain: 'line_item',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(estimated_units)',
        inputs: [],
        short_description: 'Total estimated units to deliver',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.actual_units': {
      sql: 'total_actual_units',
      type: 'number',
      aggType: 'sum',
      title: 'Actual Units',
      format: 'number',
      description: 'Total actual units delivered from platform data',
      meta: {
        category: 'delivery',
        grain: 'line_item',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_actual_units)',
        inputs: [],
        short_description: 'Total actual units delivered from platform data',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.actual_units'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.actual_spend': {
      sql: 'total_actual_spend',
      type: 'number',
      aggType: 'sum',
      title: 'Actual Spend',
      format: 'currency',
      description: 'Total actual media spend from platform data',
      meta: {
        category: 'spend',
        grain: 'line_item',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_actual_spend)',
        inputs: [],
        short_description: 'Total actual media spend from platform data',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.actual_spend'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.uncapped_effective_revenue': {
      sql: 'line_items.total_uncapped_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Uncapped Effective Revenue',
      format: 'currency',
      description:
        'Uncapped revenue net of referral commission. This is the revenue the agency retains from delivered units after referral payouts. Not capped at the line item price; can exceed effective_price if more units are delivered than estimated.',
      meta: {
        category: 'revenue',
        grain: 'line_item',
        type: 'effective',
        supports_rollup: true,
        formula: 'total_uncapped_revenue * (1 - referral_commission_rate)',
        inputs: ['line_items.uncapped_revenue', 'line_items.referral_commission_rate'],
        short_description:
          'Uncapped revenue net of referral commission. Can exceed effective_price if overdelivered',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.uncapped_effective_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.target_revenue': {
      sql: 'line_items.estimated_units * line_items.effective_unit_price',
      type: 'number',
      aggType: 'sum',
      title: 'Target Revenue',
      format: 'currency',
      description:
        'Target revenue = estimated_units * effective_unit_price. NULL for management_fee/zero_dollar (excluded from SUM).',
      meta: {
        category: 'revenue',
        grain: 'line_item',
        type: 'target',
        supports_rollup: true,
        formula: 'estimated_units * effective_unit_price',
        inputs: ['line_items.estimated_units', 'line_items.effective_unit_price'],
        short_description:
          'Target revenue (estimated_units * effective_unit_price). NULL for management_fee/zero_dollar items',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.media_cost': {
      sql: 'total_media_cost',
      type: 'number',
      aggType: 'sum',
      title: 'Media Cost',
      format: 'currency',
      description:
        'Total media cost (COGS). Equals actual_spend for standard line items but is 0 for management_fee items. Use this for margin calculations (revenue - COGS). Use actual_spend for total platform expenditure.',
      meta: {
        category: 'spend',
        grain: 'line_item',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_media_cost)',
        inputs: [],
        short_description: 'Total media cost (COGS). 0 for management_fee items',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.media_cost'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.uncapped_revenue': {
      sql: 'total_uncapped_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Uncapped Revenue',
      format: 'currency',
      description: 'Raw uncapped revenue before commission adjustment. Not limited by any cap.',
      meta: {
        category: 'revenue',
        grain: 'line_item',
        period: 'lifetime',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_uncapped_revenue)',
        inputs: [],
        short_description: 'Raw uncapped revenue before referral commission',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.uncapped_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.billable_revenue': {
      sql: 'total_billable_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Billable Revenue',
      format: 'currency',
      description:
        "Revenue billable to client. Applies monthly or lifetime cap based on line item's rollover setting.",
      meta: {
        category: 'revenue',
        grain: 'line_item',
        period: 'lifetime',
        type: 'actual',
        supports_rollup: true,
        formula: 'SUM(total_billable_revenue)',
        inputs: [],
        short_description: 'Revenue billable to client (capped per rollover setting)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.effective_billable_revenue': {
      sql: 'line_items.total_billable_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Effective Billable Revenue',
      format: 'currency',
      description: 'Billable revenue net of referral commission.',
      meta: {
        category: 'revenue',
        grain: 'line_item',
        period: 'lifetime',
        type: 'effective',
        supports_rollup: true,
        formula: 'total_billable_revenue * (1 - referral_commission_rate)',
        inputs: ['line_items.billable_revenue', 'line_items.referral_commission_rate'],
        short_description: 'Billable revenue net of referral commission',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.monthly_capped_actual_revenue': {
      sql: 'total_monthly_capped_actual_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Monthly Capped Revenue',
      format: 'currency',
      description:
        "Sum of per-month capped actual revenue across the flight. Each month's actual revenue is capped at that month's planned revenue total, then summed across all months.",
      meta: {
        category: 'revenue',
        grain: 'line_item',
        period: 'monthly',
        type: 'capped_actual',
        supports_rollup: true,
        formula: 'SUM(total_monthly_capped_actual_revenue)',
        inputs: [],
        short_description:
          'Sum of per-month capped actual revenue across the flight. Each month capped at its monthly revenue cap',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.monthly_capped_actual_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.effective_monthly_capped_actual_revenue': {
      sql: 'line_items.total_monthly_capped_actual_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Effective Monthly Capped Revenue',
      format: 'currency',
      description: 'Monthly capped revenue net of referral commission.',
      meta: {
        category: 'revenue',
        grain: 'line_item',
        period: 'monthly',
        type: 'effective_capped_actual',
        supports_rollup: true,
        formula: 'total_monthly_capped_actual_revenue * (1 - referral_commission_rate)',
        inputs: ['line_items.monthly_capped_actual_revenue', 'line_items.referral_commission_rate'],
        short_description: 'Monthly capped revenue net of referral commission',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.effective_monthly_capped_actual_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.lifetime_capped_actual_revenue': {
      sql: 'LEAST(line_items.total_uncapped_revenue, line_items.price)',
      type: 'number',
      aggType: 'sum',
      title: 'Lifetime Capped Revenue',
      format: 'currency',
      description:
        'Uncapped revenue capped at line item price (lifetime). LEAST(uncapped_revenue, price) per line item, then summed.',
      meta: {
        category: 'revenue',
        grain: 'line_item',
        period: 'lifetime',
        type: 'capped_actual',
        supports_rollup: true,
        formula: 'SUM(LEAST(total_uncapped_revenue, price))',
        inputs: ['line_items.uncapped_revenue', 'line_items.price'],
        short_description: 'Uncapped revenue capped at line item contract price (lifetime cap)',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.lifetime_capped_actual_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.effective_lifetime_capped_actual_revenue': {
      sql: 'LEAST(line_items.total_uncapped_revenue, line_items.price) * (1 - COALESCE(line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Effective Lifetime Capped Revenue',
      format: 'currency',
      description: 'Lifetime capped revenue net of referral commission.',
      meta: {
        category: 'revenue',
        grain: 'line_item',
        period: 'lifetime',
        type: 'effective_capped_actual',
        supports_rollup: true,
        formula: 'LEAST(total_uncapped_revenue, price) * (1 - referral_commission_rate)',
        inputs: [
          'line_items.uncapped_revenue',
          'line_items.price',
          'line_items.referral_commission_rate'
        ],
        short_description:
          'Lifetime capped revenue net of referral commission. Most conservative revenue figure',
        explanation:
          'Takes the lesser of total uncapped revenue and the line item contract price, then applies the referral commission deduction.',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.effective_lifetime_capped_actual_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.current_planned_units': {
      sql: 'current_planned_units',
      type: 'number',
      aggType: 'sum',
      title: 'Current Planned Units',
      format: 'number',
      description:
        'Planned units through yesterday. Pre-aggregated from line_item_metrics_daily WHERE day < current_date. This represents what should have been delivered by now according to the pacing schedule. Updates daily as the refresh function recalculates.',
      meta: {
        category: 'pacing',
        grain: 'line_item',
        period: 'current',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(current_planned_units)',
        inputs: [],
        short_description: 'Planned units through yesterday from pacing schedule. Updates daily',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.planned_units'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.required_daily_units_block': {
      sql: 'required_daily_units_block',
      type: 'number',
      aggType: 'sum',
      title: 'Required Daily Units (Block)',
      format: 'number',
      description:
        'Units per day required to hit the current pacing block target, based on actuals through yesterday.',
      meta: {
        category: 'pacing',
        period: 'current',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(required_daily_units_block)',
        inputs: [],
        grain: 'line_item',
        short_description: 'Required daily units to hit current pacing block target',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.required_daily_units_month': {
      sql: 'required_daily_units_month',
      type: 'number',
      aggType: 'sum',
      title: 'Required Daily Units (Month)',
      format: 'number',
      description:
        'Units per day required to hit the current month target, based on actuals through yesterday.',
      meta: {
        category: 'pacing',
        period: 'monthly',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(required_daily_units_month)',
        inputs: [],
        grain: 'line_item',
        short_description: 'Required daily units to hit current month target',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.required_daily_units_lifetime': {
      sql: 'required_daily_units_lifetime',
      type: 'number',
      aggType: 'sum',
      title: 'Required Daily Units (Lifetime)',
      format: 'number',
      description:
        'Units per day required to hit the lifetime target, based on actuals through yesterday.',
      meta: {
        category: 'pacing',
        period: 'lifetime',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(required_daily_units_lifetime)',
        inputs: [],
        grain: 'line_item',
        short_description: 'Required daily units to hit lifetime target',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.planned_daily_units_block': {
      sql: 'planned_daily_units_block',
      type: 'number',
      aggType: 'sum',
      title: 'Planned Daily Units (Block)',
      format: 'number',
      description:
        'Original daily planned rate for the current pacing block (block_planned_units / block_total_days). Baseline for severity comparison against required_daily_units_block.',
      meta: {
        category: 'pacing',
        period: 'current',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(planned_daily_units_block)',
        inputs: [],
        grain: 'line_item',
        short_description: 'Original daily planned rate for current pacing block',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.planned_daily_units_month': {
      sql: 'planned_daily_units_month',
      type: 'number',
      aggType: 'sum',
      title: 'Planned Daily Units (Month)',
      format: 'number',
      description:
        'Original daily planned rate for the current month (month_planned_units / month_total_days). Baseline for severity comparison against required_daily_units_month.',
      meta: {
        category: 'pacing',
        period: 'monthly',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(planned_daily_units_month)',
        inputs: [],
        grain: 'line_item',
        short_description: 'Original daily planned rate for current month',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.planned_daily_units_lifetime': {
      sql: 'planned_daily_units_lifetime',
      type: 'number',
      aggType: 'sum',
      title: 'Planned Daily Units (Lifetime)',
      format: 'number',
      description:
        'Original daily planned rate for the lifetime (estimated_units / total_days). Baseline for severity comparison against required_daily_units_lifetime.',
      meta: {
        category: 'pacing',
        period: 'lifetime',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(planned_daily_units_lifetime)',
        inputs: [],
        grain: 'line_item',
        short_description: 'Original daily planned rate for lifetime',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.current_planned_spend': {
      sql: 'current_planned_spend',
      type: 'number',
      aggType: 'sum',
      title: 'Current Planned Spend',
      format: 'currency',
      description:
        'Planned spend through yesterday. Pre-aggregated from line_item_metrics_daily WHERE day < current_date. Denominator for spend_pacing ratio. Updates daily.',
      meta: {
        category: 'pacing',
        grain: 'line_item',
        period: 'current',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(current_planned_spend)',
        inputs: [],
        short_description: 'Planned spend through yesterday from pacing schedule. Updates daily',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.planned_spend'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.effective_planned_revenue': {
      sql: 'line_items.current_planned_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0))',
      type: 'number',
      aggType: 'sum',
      title: 'Effective Planned Revenue',
      format: 'currency',
      description:
        'Planned revenue through yesterday, net of referral commission. Used as the denominator for delivery pacing at the line-item level. Updates daily.',
      meta: {
        category: 'pacing',
        grain: 'line_item',
        period: 'current',
        type: 'effective',
        supports_rollup: true,
        formula: 'current_planned_revenue * (1 - referral_commission_rate)',
        inputs: ['line_items.planned_revenue', 'line_items.referral_commission_rate'],
        short_description: 'Planned revenue through yesterday, net of referral commission',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.current_effective_planned_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.planned_revenue': {
      sql: 'current_planned_revenue',
      type: 'number',
      aggType: 'sum',
      title: 'Planned Revenue',
      format: 'currency',
      description:
        'Planned revenue through yesterday, before commission. Pre-aggregated from line_item_metrics_daily WHERE day < current_date. Updates daily.',
      meta: {
        category: 'pacing',
        grain: 'line_item',
        period: 'current',
        type: 'planned',
        supports_rollup: true,
        formula: 'SUM(current_planned_revenue)',
        inputs: [],
        short_description: 'Planned revenue through yesterday (before commission). Updates daily',
        source_tables: ['metrics.line_items'],
        rolled_up_from: 'line_item_metrics_daily.current_planned_revenue'
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.margin_actual': {
      sql: '(line_items.effective_billable_revenue - line_items.media_cost) / NULLIF(line_items.effective_billable_revenue, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Actual Margin',
      format: 'percent',
      description:
        'Actual margin percentage. (Effective billable revenue - COGS) / Effective billable revenue',
      meta: {
        category: 'margin',
        grain: 'line_item',
        type: 'actual',
        supports_rollup: false,
        formula:
          '(effective_billable_revenue - media_cost) / NULLIF(effective_billable_revenue, 0)',
        inputs: ['line_items.effective_billable_revenue', 'line_items.media_cost'],
        short_description:
          'Actual margin percentage: (effective billable revenue - COGS) / effective billable revenue',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.unit_cost_actual': {
      sql: 'line_items.actual_spend / NULLIF(line_items.actual_units, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Actual Unit Cost',
      format: 'currency',
      description: 'Actual cost per unit delivered. Spend / Units',
      meta: {
        category: 'cost',
        grain: 'line_item',
        type: 'actual',
        supports_rollup: false,
        formula: 'actual_spend / NULLIF(actual_units, 0)',
        inputs: ['line_items.actual_spend', 'line_items.actual_units'],
        short_description: 'Actual cost per unit delivered',
        source_tables: ['metrics.line_items'],
        is_unit_rate: true
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.referral_commission_amount': {
      sql: 'line_items.price * COALESCE(line_items.referral_commission_rate, 0)',
      type: 'number',
      aggType: 'sum',
      title: 'Referral Commission Amount',
      format: 'currency',
      description: 'Dollar amount of referral commission. Price * commission rate',
      meta: {
        category: 'financial',
        grain: 'line_item',
        supports_rollup: true,
        formula: 'price * referral_commission_rate',
        inputs: ['line_items.price', 'line_items.referral_commission_rate'],
        short_description: 'Dollar amount of referral commission on contract price',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.gross_unit_price': {
      sql: 'line_items.unit_price / NULLIF(1 - line_items.agency_markup_rate, 0)',
      type: 'number',
      aggType: 'avg',
      title: 'Gross Unit Price',
      format: 'currency',
      description: 'Client-facing unit price including agency markup',
      meta: {
        category: 'gross',
        grain: 'line_item',
        supports_rollup: false,
        formula: 'unit_price / (1 - agency_markup_rate)',
        inputs: ['line_items.unit_price', 'line_items.agency_markup_rate'],
        short_description: 'Client-facing per-unit price including agency markup',
        source_tables: ['metrics.line_items'],
        is_unit_rate: true
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.delivery_pacing': {
      sql: 'line_items.actual_units / NULLIF(line_items.current_planned_units, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Delivery Pacing',
      format: 'percent',
      description: 'Delivery pacing ratio. Actual units / planned units through yesterday',
      meta: {
        category: 'pacing',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: 'actual_units / NULLIF(current_planned_units, 0)',
        inputs: ['line_items.actual_units', 'line_items.current_planned_units'],
        short_description: 'Delivery pacing (actual_units / planned_units through yesterday)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.percent_units_delivered': {
      sql: 'line_items.actual_units / NULLIF(line_items.estimated_units, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Percent Units Delivered',
      format: 'percent',
      description: 'Percentage of estimated units delivered',
      meta: {
        category: 'pacing',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: 'actual_units / NULLIF(estimated_units, 0)',
        inputs: ['line_items.actual_units', 'line_items.estimated_units'],
        short_description: 'Percentage of estimated units delivered',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.units_remaining': {
      sql: 'GREATEST(0, line_items.estimated_units - line_items.total_actual_units)',
      type: 'number',
      aggType: 'sum',
      title: 'Units Remaining',
      format: 'number',
      description:
        'Estimated units minus actual units delivered, floored at 0. Per-row GREATEST before summing.',
      meta: {
        category: 'delivery',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: true,
        formula: 'SUM(GREATEST(0, estimated_units - total_actual_units))',
        inputs: ['line_items.estimated_units', 'line_items.actual_units'],
        short_description: 'Estimated units minus actual units, floored at 0',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.spend_pacing': {
      sql: 'line_items.actual_spend / NULLIF(line_items.current_planned_spend, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Spend Pacing',
      format: 'percent',
      description: 'Spend pacing ratio. Actual spend / planned spend through yesterday',
      meta: {
        category: 'pacing',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: 'actual_spend / NULLIF(current_planned_spend, 0)',
        inputs: ['line_items.actual_spend', 'line_items.current_planned_spend'],
        short_description: 'Spend pacing (actual_spend / planned_spend through yesterday)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.percent_budget_spent': {
      sql: 'line_items.actual_spend / NULLIF(line_items.media_budget, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Percent Budget Spent',
      format: 'percent',
      description: 'Percentage of media budget spent',
      meta: {
        category: 'pacing',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: 'actual_spend / NULLIF(media_budget, 0)',
        inputs: ['line_items.actual_spend', 'line_items.media_budget'],
        short_description: 'Percentage of media budget spent',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.budget_remaining': {
      sql: 'GREATEST(0, line_items.media_budget - line_items.total_actual_spend)',
      type: 'number',
      aggType: 'sum',
      title: 'Budget Remaining',
      format: 'currency',
      description:
        'Media budget minus actual spend, floored at 0. Per-row GREATEST before summing.',
      meta: {
        category: 'spend',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: true,
        formula: 'SUM(GREATEST(0, media_budget - total_actual_spend))',
        inputs: ['line_items.media_budget', 'line_items.actual_spend'],
        short_description: 'Media budget minus actual spend, floored at 0',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.margin_variance': {
      sql: '(line_items.margin_actual - line_items.planned_margin) / NULLIF(line_items.planned_margin, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Margin Variance',
      format: 'percent',
      description: 'Variance between actual and planned margin',
      meta: {
        category: 'margin',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: '(margin_actual - planned_margin) / NULLIF(planned_margin, 0)',
        inputs: ['line_items.margin_actual', 'line_items.planned_margin'],
        short_description: 'Percentage variance between actual and planned margin',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.unit_cost_variance': {
      sql: '(line_items.unit_cost_actual - line_items.planned_unit_cost) / NULLIF(line_items.planned_unit_cost, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Unit Cost Variance',
      format: 'percent',
      description: 'Variance between actual and planned unit cost',
      meta: {
        category: 'cost',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: '(unit_cost_actual - planned_unit_cost) / NULLIF(planned_unit_cost, 0)',
        inputs: ['line_items.unit_cost_actual', 'line_items.planned_unit_cost'],
        short_description: 'Percentage variance between actual and planned unit cost',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.percent_revenue_delivered': {
      sql: 'line_items.effective_billable_revenue / NULLIF(line_items.effective_price, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Percent Revenue Delivered',
      format: 'percent',
      description: 'Percentage of effective price delivered as billable revenue',
      meta: {
        category: 'pacing',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: 'effective_billable_revenue / NULLIF(effective_price, 0)',
        inputs: ['line_items.effective_billable_revenue', 'line_items.effective_price'],
        short_description: 'Percentage of effective price delivered as billable revenue',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.revenue_remaining': {
      sql: 'GREATEST(0, line_items.effective_price - line_items.total_billable_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0)))',
      type: 'number',
      aggType: 'sum',
      title: 'Revenue Remaining',
      format: 'currency',
      description:
        'Effective price minus effective billable revenue, floored at 0. Per-row GREATEST before summing.',
      meta: {
        category: 'revenue',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: true,
        formula: 'SUM(GREATEST(0, effective_price - effective_billable_revenue))',
        inputs: [
          'line_items.effective_price',
          'line_items.billable_revenue',
          'line_items.referral_commission_rate'
        ],
        short_description: 'Effective price minus effective billable revenue, floored at 0',
        explanation:
          'Computes effective_price and effective_billable_revenue per row, takes the difference floored at 0, then sums across line items.',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.target_margin_variance': {
      sql: '(line_items.target_margin - line_items.planned_margin) / NULLIF(line_items.target_margin, 0)',
      type: 'number',
      aggType: 'number',
      title: 'Target Margin Variance',
      format: 'percent',
      description: 'Variance between target and planned margin',
      meta: {
        category: 'margin',
        grain: 'line_item',
        type: 'derived',
        supports_rollup: false,
        formula: '(target_margin - planned_margin) / NULLIF(target_margin, 0)',
        inputs: ['line_items.target_margin', 'line_items.planned_margin'],
        short_description: 'Percentage variance between target and planned margin',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.earliest_start_date': {
      sql: 'start_date',
      type: 'number',
      aggType: 'min',
      title: 'Earliest Start Date',
      description: 'Earliest line item start date across filtered set',
      meta: {
        category: 'utility',
        grain: 'line_item',
        supports_rollup: true,
        formula: 'MIN(start_date)',
        inputs: [],
        short_description: 'Earliest line item start date across filtered set',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.latest_end_date': {
      sql: 'end_date',
      type: 'number',
      aggType: 'max',
      title: 'Latest End Date',
      description: 'Latest line item end date across filtered set',
      meta: {
        category: 'utility',
        grain: 'line_item',
        supports_rollup: true,
        formula: 'MAX(end_date)',
        inputs: [],
        short_description: 'Latest line item end date across filtered set',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.weighted_planned_cost': {
      sql: 'line_items.estimated_units * line_items.planned_unit_cost',
      type: 'number',
      aggType: 'sum',
      title: 'Weighted Planned Cost',
      format: 'currency',
      description:
        'Sum of (estimated_units * planned_unit_cost) for overall planned margin calculation',
      meta: {
        category: 'margin',
        grain: 'line_item',
        type: 'planned',
        supports_rollup: true,
        formula: 'estimated_units * planned_unit_cost',
        inputs: ['line_items.estimated_units', 'line_items.planned_unit_cost'],
        short_description:
          'Sum of (estimated_units * planned_unit_cost). Building block for overall planned margin',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.weighted_target_cost': {
      sql: 'line_items.estimated_units * line_items.target_unit_cost',
      type: 'number',
      aggType: 'sum',
      title: 'Weighted Target Cost',
      format: 'currency',
      description:
        'Sum of (estimated_units * target_unit_cost) for overall target margin calculation',
      meta: {
        category: 'margin',
        grain: 'line_item',
        type: 'target',
        supports_rollup: true,
        formula: 'estimated_units * target_unit_cost',
        inputs: ['line_items.estimated_units', 'line_items.target_unit_cost'],
        short_description:
          'Sum of (estimated_units * target_unit_cost). Building block for overall target margin',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.weighted_target_revenue': {
      sql: 'line_items.estimated_units * line_items.target_unit_price',
      type: 'number',
      aggType: 'sum',
      title: 'Weighted Target Revenue',
      format: 'currency',
      description:
        'Sum of (estimated_units * target_unit_price) for overall target margin calculation',
      meta: {
        category: 'margin',
        grain: 'line_item',
        type: 'target',
        supports_rollup: true,
        formula: 'estimated_units * target_unit_price',
        inputs: ['line_items.estimated_units', 'line_items.target_unit_price'],
        short_description:
          'Sum of (estimated_units * target_unit_price). Building block for overall target margin',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.media_plans_count_sum': {
      sql: 'media_plans_count',
      type: 'number',
      aggType: 'sum',
      title: 'Media Plans Count Sum',
      meta: {
        category: 'utility',
        grain: 'line_item',
        supports_rollup: true,
        formula: 'SUM(media_plans_count)',
        inputs: [],
        short_description: 'Total media plans across filtered line items',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.active_media_plans_count_sum': {
      sql: 'active_media_plans_count',
      type: 'number',
      aggType: 'sum',
      title: 'Active Media Plans Count Sum',
      meta: {
        category: 'utility',
        grain: 'line_item',
        supports_rollup: true,
        formula: 'SUM(active_media_plans_count)',
        inputs: [],
        short_description: 'Total active media plans across filtered line items',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.active_buys_outside_plan_count_sum': {
      sql: 'active_buys_outside_plan_count',
      type: 'number',
      aggType: 'sum',
      title: 'Active Buys Outside Plan Count Sum',
      meta: {
        category: 'utility',
        grain: 'line_item',
        supports_rollup: true,
        formula: 'SUM(active_buys_outside_plan_count)',
        inputs: [],
        short_description:
          'Total active media buys not linked to a plan across filtered line items',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry,
    'line_items.num_media_buys_sum': {
      sql: 'num_media_buys',
      type: 'number',
      aggType: 'sum',
      title: 'Media Buys Count Sum',
      meta: {
        category: 'utility',
        grain: 'line_item',
        supports_rollup: true,
        formula: 'SUM(num_media_buys)',
        inputs: [],
        short_description: 'Total media buys across filtered line items',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeMeasureEntry
  },
  dimensions: {
    'line_items.id': {
      sql: 'id',
      type: 'string',
      title: 'Id',
      description: 'Unique line item identifier',
      primaryKey: true,
      meta: {
        short_description: 'Unique line item identifier',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.name': {
      sql: 'name',
      type: 'string',
      title: 'Line Item Name',
      description: 'Name of the line item',
      meta: {
        short_description: 'Display name of the line item',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.agency_markup_rate': {
      sql: 'agency_markup_rate',
      type: 'number',
      title: 'Agency Markup Rate',
      format: 'percent',
      description: 'Agency markup rate applied to net price to get gross (client-facing) price',
      meta: {
        short_description: 'Agency markup rate (net to gross price)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.campaign_id': {
      sql: 'campaign_id',
      type: 'string',
      title: 'Campaign ID',
      description: 'Parent campaign identifier',
      meta: {
        short_description: 'Parent campaign identifier',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.line_item_type': {
      sql: 'line_item_type',
      type: 'string',
      title: 'Line Item Type',
      description: 'Type of line item (e.g. media, management_fee, zero_dollar)',
      meta: {
        short_description:
          'Type of line item: media, management_fee, or zero_dollar. Affects media_cost and margin calculations',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.pacing_details': {
      sql: 'pacing_details',
      type: 'string',
      title: 'Pacing Details',
      description: 'JSON details for pacing configuration',
      meta: {
        short_description: 'JSON configuration for pacing schedule',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.pacing_type': {
      sql: 'pacing_type',
      type: 'string',
      title: 'Pacing Type',
      description: 'How spend is distributed over time (e.g. even, front-loaded, custom)',
      meta: {
        short_description: 'How spend is distributed over time (even, front-loaded, or custom)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.planned_unit_cost': {
      sql: 'planned_unit_cost',
      type: 'number',
      title: 'Planned Unit Cost',
      format: 'currency',
      description: 'Expected cost per unit based on media plan',
      meta: {
        category: 'cost',
        short_description: 'Expected cost per unit from the media plan',
        source_tables: ['metrics.line_items'],
        is_unit_rate: true
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.referral_commission_rate': {
      sql: 'referral_commission_rate',
      type: 'number',
      title: 'Referral Commission Rate',
      format: 'percent',
      description: 'Percentage of revenue paid to referral partners',
      meta: {
        short_description: 'Percentage of revenue paid to referral partners',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.strategy_id': {
      sql: 'strategy_id',
      type: 'string',
      title: 'Strategy ID',
      description: 'Associated strategy identifier',
      meta: {
        short_description: 'Associated strategy identifier',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.unit_price': {
      sql: 'unit_price',
      type: 'number',
      title: 'Unit Price',
      format: 'currency',
      description: 'Per-unit price charged to client',
      meta: {
        short_description: 'Per-unit price charged to client (before referral commission)',
        source_tables: ['metrics.line_items'],
        is_unit_rate: true
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.price_value': {
      sql: 'price',
      type: 'number',
      title: 'Price (Value)',
      format: 'currency',
      description:
        'Total line item price as a dimension for cross-cube references (e.g. lifetime capping)',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.price',
        short_description: 'Total line item contract price (lifetime revenue cap)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.end_date': {
      sql: 'end_date',
      type: 'time',
      title: 'End Date',
      description: 'Line item end date',
      meta: {
        short_description: 'Line item flight end date',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.start_date': {
      sql: 'start_date',
      type: 'time',
      title: 'Start Date',
      description: 'Line item start date',
      meta: {
        short_description: 'Line item flight start date',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.last_activity_date': {
      sql: 'last_activity_date',
      type: 'time',
      title: 'Last Activity Date',
      description: 'Most recent date the line item delivered or spent',
      meta: {
        short_description: 'Most recent delivery/spend date for the line item',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.planned_margin': {
      sql: 'planned_margin',
      type: 'number',
      title: 'Planned Margin',
      format: 'percent',
      description:
        'Expected margin percentage derived from line-item static pricing: (unit_price - planned_unit_cost) / unit_price. This is NOT derived from daily pacing data — it reflects the margin built into the contract terms at the time the line item was created. For custom-paced items, the daily pacing schedule may imply a different effective margin on any given day. Compare with margin_actual for realized performance.',
      meta: {
        category: 'margin',
        type: 'planned',
        formula: '(unit_price - planned_unit_cost) / unit_price',
        inputs: ['line_items.unit_price', 'line_items.planned_unit_cost'],
        short_description:
          'Expected margin from contract pricing: (unit_price - planned_unit_cost) / unit_price. Not from daily pacing',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.target_margin': {
      sql: 'target_margin',
      type: 'number',
      title: 'Target Margin',
      format: 'percent',
      description:
        'Target margin percentage goal for the line item. This is the desired profit margin set during planning.',
      meta: {
        category: 'margin',
        type: 'target',
        short_description: 'Target margin percentage goal set during planning',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.target_unit_cost': {
      sql: 'target_unit_cost',
      type: 'number',
      title: 'Target Unit Cost',
      format: 'currency',
      description: 'Target cost per unit goal for the line item',
      meta: {
        short_description: 'Target cost per unit goal set during planning',
        source_tables: ['metrics.line_items'],
        is_unit_rate: true
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.is_proposal': {
      sql: 'is_proposal',
      type: 'boolean',
      title: 'Is Proposal',
      description: 'True if line item is from proposal (not yet promoted to execution).',
      meta: {
        short_description: 'True if line item is from a proposal (not yet promoted to execution)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.is_failure_to_launch': {
      sql: '(NOT line_items.is_proposal AND line_items.estimated_units > 0 AND line_items.media_budget > 0 AND line_items.start_date <= CURRENT_DATE - 1 AND line_items.end_date >= CURRENT_DATE AND COALESCE(line_items.total_actual_units, 0) = 0 AND COALESCE(line_items.total_actual_spend, 0) = 0)',
      type: 'boolean',
      title: 'Failure to Launch',
      description: 'P1 critical: line item is in flight but has zero delivery and zero spend.',
      meta: {
        short_description: 'P1: in flight, zero delivery, zero spend',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.schedule_elapsed_pct': {
      sql: '(CURRENT_DATE - 1 - line_items.start_date)::numeric / NULLIF(line_items.end_date - line_items.start_date, 0)',
      type: 'number',
      title: 'Schedule Elapsed %',
      format: 'percent',
      description:
        'Fraction of the flight elapsed through yesterday. (CURRENT_DATE - 1 - start_date) / (end_date - start_date).',
      meta: {
        formula: '(CURRENT_DATE - 1 - start_date) / NULLIF(end_date - start_date, 0)',
        short_description: 'Fraction of flight elapsed through yesterday',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.is_critical_underdelivery': {
      sql: 'COALESCE( NOT line_items.is_proposal AND line_items.estimated_units > 0 AND line_items.media_budget > 0 AND line_items.end_date >= CURRENT_DATE AND line_items.delivery_pacing_value <= 0.80 AND line_items.schedule_elapsed_pct > 0.50 AND line_items.units_remaining_value > 1.5 * GREATEST(0, line_items.estimated_units - line_items.current_planned_units), false )',
      type: 'boolean',
      title: 'Critical Underdelivery',
      description:
        'P1 critical: in-flight line item is at or below 80% of plan, past the halfway point of its schedule, and needs over 1.5x its current daily average to land.',
      meta: {
        short_description: 'P1: pacing <= 80%, past halfway, recovery rate > 1.5x current avg',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.is_runaway_spend': {
      sql: 'COALESCE( NOT line_items.is_proposal AND line_items.estimated_units > 0 AND line_items.media_budget > 0 AND line_items.spend_pacing_value > line_items.delivery_pacing_value + 0.20 AND line_items.unit_cost_actual_value > line_items.planned_unit_cost * 1.25 AND line_items.schedule_elapsed_pct > 0.25, false )',
      type: 'boolean',
      title: 'Runaway Spend',
      description:
        'P1 critical: spend pacing exceeds delivery pacing by more than 20 points and actual unit cost is more than 1.25x plan, after the first 25% of the flight.',
      meta: {
        short_description:
          'P1: spend pacing > delivery pacing + 20pts, unit cost > 1.25x plan, past 25% schedule',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.is_critical_failure': {
      sql: 'COALESCE( NOT line_items.is_proposal AND line_items.estimated_units > 0 AND line_items.media_budget > 0 AND line_items.delivery_pacing_value <= 0.80 AND line_items.schedule_elapsed_pct > 0.50 AND line_items.margin_actual_value < line_items.planned_margin - 0.25, false )',
      type: 'boolean',
      title: 'Critical Failure',
      description:
        'P1 critical: delivery pacing is at or below 80% AND margin is more than 25 points below plan, past the halfway point of the flight.',
      meta: {
        short_description: 'P1: pacing <= 80% and margin > 25pts below plan, past 50% schedule',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.is_not_running': {
      sql: '(NOT line_items.is_proposal AND line_items.last_activity_date IS NOT NULL AND line_items.last_activity_date <= CURRENT_DATE - 3 AND line_items.end_date >= CURRENT_DATE AND (line_items.estimated_units - COALESCE(line_items.total_actual_units, 0)) > 0 AND (line_items.media_budget - COALESCE(line_items.total_actual_spend, 0)) > 0 AND COALESCE(line_items.required_daily_units_block > 0, true))',
      type: 'boolean',
      title: 'Not Running',
      description:
        'Line item previously delivered but has stopped for at least 3 days while still in flight, with units and budget remaining and its current pacing block still expecting delivery.',
      meta: {
        short_description:
          'In flight with block demand remaining, but stopped delivering 3+ days ago',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.has_p1_issue': {
      sql: 'COALESCE(line_items.is_failure_to_launch OR line_items.is_not_running OR line_items.is_critical_underdelivery OR line_items.is_runaway_spend OR line_items.is_critical_failure, false)',
      type: 'boolean',
      title: 'Has P1 Issue',
      description:
        'True if any P1 condition is true: failure_to_launch, not_running, critical_underdelivery, runaway_spend, or critical_failure.',
      meta: {
        short_description:
          'P1 rollup: failure_to_launch OR not_running OR critical_underdelivery OR runaway_spend OR critical_failure',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.is_underdelivery_risk': {
      sql: 'COALESCE( NOT line_items.is_proposal AND line_items.estimated_units > 0 AND line_items.media_budget > 0 AND line_items.end_date >= CURRENT_DATE AND line_items.start_date <= CURRENT_DATE - 1 AND line_items.delivery_pacing_value < 0.90 AND (line_items.schedule_elapsed_pct <= 0.50 OR line_items.delivery_pacing_value > 0.80) AND line_items.units_remaining_value <= 1.5 * GREATEST(0, line_items.estimated_units - line_items.current_planned_units), false )',
      type: 'boolean',
      title: 'Underdelivery Risk',
      description:
        'P2 urgent: in-flight line item is pacing below 90% of plan but still recoverable — the negated complement of P1 critical_underdelivery, so no behind-pace line falls through both.',
      meta: {
        short_description: 'P2: pacing < 90%, still urgent (not critical), and recoverable',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.is_approaching_revenue_cap': {
      sql: 'COALESCE( NOT line_items.is_proposal AND line_items.estimated_units > 0 AND line_items.media_budget > 0 AND line_items.end_date >= CURRENT_DATE AND line_items.start_date <= CURRENT_DATE - 1 AND line_items.delivery_pacing_value > 1.0 AND (line_items.end_date - CURRENT_DATE) > 3, false )',
      type: 'boolean',
      title: 'Approaching Revenue Cap',
      description:
        'P2 urgent: over-delivering line item (delivery pacing above 1.0) with more than 3 days of flight remaining, risking early completion of contracted units and uncapped, margin-eroding spend.',
      meta: {
        short_description: 'P2: pacing > 1.0 with > 3 days of flight left (early-completion risk)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.is_overdelivery_overspend': {
      sql: 'COALESCE( NOT line_items.is_proposal AND line_items.estimated_units > 0 AND line_items.media_budget > 0 AND line_items.end_date >= CURRENT_DATE AND line_items.start_date <= CURRENT_DATE - 1 AND line_items.delivery_pacing_value > 1.15 AND line_items.spend_pacing_value > 1.15, false )',
      type: 'boolean',
      title: 'Overdelivery Overspend',
      description:
        'P2 urgent: in-flight line item running well above schedule on both delivery and spend (each pacing above 1.15) — over-delivering while burning budget faster than planned.',
      meta: {
        short_description: 'P2: delivery pacing > 1.15 AND spend pacing > 1.15',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.is_margin_erosion': {
      sql: 'COALESCE( NOT line_items.is_proposal AND line_items.estimated_units > 0 AND line_items.media_budget > 0 AND line_items.end_date >= CURRENT_DATE AND line_items.start_date <= CURRENT_DATE - 1 AND line_items.delivery_pacing_value BETWEEN 0.90 AND 1.10 AND line_items.unit_cost_actual_value > line_items.planned_unit_cost * 1.10 AND line_items.margin_actual_value < line_items.planned_margin - 0.05, false )',
      type: 'boolean',
      title: 'Margin Erosion',
      description:
        'P2 urgent: in-flight line item delivering on pace (delivery pacing within ±10 points of plan) but at an actual unit cost more than 1.10x plan and realized margin more than 5 points below plan — eroding margin while units land on schedule.',
      meta: {
        short_description:
          'P2: on pace (pacing 0.90-1.10), unit cost > 1.10x plan, margin > 5pts below plan',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.has_p2_issue': {
      sql: 'COALESCE((line_items.is_underdelivery_risk OR line_items.is_margin_erosion OR line_items.is_overdelivery_overspend OR line_items.is_approaching_revenue_cap) AND NOT line_items.has_p1_issue, false)',
      type: 'boolean',
      title: 'Has P2 Issue',
      description:
        'True if any P2 condition is true (underdelivery_risk, margin_erosion, overdelivery_overspend, or approaching_revenue_cap) AND the line item is not already carrying a P1 issue (first-match-wins priority).',
      meta: {
        short_description:
          'P2 rollup: (underdelivery_risk OR margin_erosion OR overdelivery_overspend OR approaching_revenue_cap) AND NOT has_p1_issue',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.media_trader_ids': {
      sql: 'line_items.media_trader_ids::text',
      type: 'string',
      title: 'Media Trader IDs',
      description:
        "Assigned media trader user IDs (uuid[]::text). Filter only — use 'contains' with a trader UUID. To group by trader use line_item_media_traders.media_trader_name.",
      meta: {
        filter_only: true,
        short_description: "Assigned media trader user IDs — 'contains'-filter by UUID only",
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.media_plans_count': {
      sql: 'media_plans_count',
      type: 'number',
      title: 'Media Plans Count',
      description: 'Number of media plans for this line item',
      meta: {
        short_description: 'Number of media plans for this line item',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.active_media_plans_count': {
      sql: 'active_media_plans_count',
      type: 'number',
      title: 'Active Media Plans Count',
      description: 'Number of media plans with active platform buys (spend yesterday)',
      meta: {
        short_description: 'Number of media plans with active platform buys (spend yesterday)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.geo': {
      sql: 'geo',
      type: 'string',
      title: 'Geo',
      description: 'Geographic targeting for this line item',
      meta: {
        short_description: 'Geographic targeting for this line item',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.targeting': {
      sql: 'targeting',
      type: 'string',
      title: 'Targeting',
      description: 'Targeting criteria for this line item',
      meta: {
        short_description: 'Targeting criteria for this line item',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.audience': {
      sql: 'audience',
      type: 'string',
      title: 'Audience',
      description: 'Target audience definition',
      meta: {
        short_description: 'Target audience definition for this line item',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.notes': {
      sql: 'notes',
      type: 'string',
      title: 'Notes',
      description: 'Free-text notes for this line item',
      meta: {
        short_description: 'Free-text notes for this line item',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.channel_name': {
      sql: 'channel_name',
      type: 'string',
      title: 'Channel',
      description: 'Advertising channel (e.g. Display, Search, Social)',
      meta: {
        short_description: 'Advertising channel (e.g. Display, Search, Social)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.tactic_name': {
      sql: 'tactic_name',
      type: 'string',
      title: 'Tactic',
      description: 'Advertising tactic within the channel',
      meta: {
        short_description: 'Advertising tactic within the channel',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.channel_tactic': {
      sql: 'channel_tactic',
      type: 'string',
      title: 'Channel / Tactic',
      description: 'Combined channel and tactic label',
      meta: {
        short_description: 'Combined channel and tactic label',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.unit_price_type_name': {
      sql: 'unit_price_type_name',
      type: 'string',
      title: 'Unit Price Type',
      description: 'Unit type for pricing (e.g. CPM, CPC, CPV)',
      meta: {
        short_description: 'Unit pricing model (e.g. CPM, CPC, CPV)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.media_platforms_json': {
      sql: 'media_platforms_json',
      type: 'string',
      title: 'Media Platforms',
      description: 'JSON array of media platform objects assigned to this line item',
      meta: {
        short_description: 'JSON array of media platform objects assigned to this line item',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.media_traders_json': {
      sql: 'media_traders_json',
      type: 'string',
      title: 'Media Traders',
      description:
        'JSON array of assigned media trader user objects. Display/filter only; to group by trader use line_item_media_traders.media_trader_name.',
      meta: {
        filter_only: true,
        short_description: 'Assigned media traders as JSON objects — display/filter only',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.num_media_buys': {
      sql: 'num_media_buys',
      type: 'number',
      title: 'Media Buys Count',
      description: 'Number of media buys associated with this line item',
      meta: {
        short_description: 'Number of media buys associated with this line item',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.active_buys_outside_plan_count': {
      sql: 'active_buys_outside_plan_count',
      type: 'number',
      title: 'Active Buys Outside Plan',
      description: 'Number of active media buys not linked to a media plan',
      meta: {
        short_description: 'Number of active media buys not linked to a media plan',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.purchase_order_number': {
      sql: 'purchase_order_number',
      type: 'string',
      title: 'Purchase Order Number',
      description: 'Client purchase order reference number',
      meta: {
        short_description: 'Client purchase order reference number',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.ad_formats': {
      sql: 'ad_formats',
      type: 'string',
      title: 'Ad Formats',
      description: 'Ad format types for this line item',
      meta: {
        short_description: 'Ad format types for this line item',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.target_unit_price': {
      sql: 'target_unit_price',
      type: 'number',
      title: 'Target Unit Price',
      format: 'currency',
      description: 'Target per-unit price goal',
      meta: {
        short_description: 'Target per-unit price goal set during planning',
        source_tables: ['metrics.line_items'],
        is_unit_rate: true
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.media_platform_ids_text': {
      sql: "',' || array_to_string(line_items.media_platform_ids, ',') || ','",
      type: 'string',
      title: 'Media Platform IDs Text',
      description: 'Comma-delimited platform IDs for contains filtering. Use with ,{id}, pattern.',
      meta: {
        formula: "',' || array_to_string(media_platform_ids, ',') || ','",
        short_description: 'Comma-delimited platform IDs for contains filtering',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.plan_status': {
      sql: "case when line_items.media_plans_count > 0 then 'complete' else 'pending' end",
      type: 'string',
      title: 'Plan Status',
      description: 'Binary plan status: complete (has media plans) or pending (no plans)',
      meta: {
        formula: "CASE WHEN media_plans_count > 0 THEN 'complete' ELSE 'pending' END",
        inputs: ['line_items.media_plans_count'],
        short_description:
          'Binary plan status: complete (has media plans) or pending (no plans yet)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.active_platform_buys_status': {
      sql: "case when line_items.media_plans_count = 0 then 'pending_plan' when line_items.active_buys_outside_plan_count > 0 and (line_items.start_date > current_date or line_items.end_date < current_date) then 'active_outside_dates' when line_items.active_media_plans_count = 0 and line_items.start_date is not null and line_items.start_date > current_date then 'pending_start' when line_items.active_buys_outside_plan_count = 0 and line_items.active_media_plans_count = line_items.media_plans_count then 'all_active' when line_items.active_buys_outside_plan_count = 0 and line_items.active_media_plans_count > 0 then 'some_active' when line_items.active_buys_outside_plan_count = 0 and line_items.active_media_plans_count = 0 then 'none_active' else null end",
      type: 'string',
      title: 'Active Platform Buys Status',
      description:
        'Computed status for active platform buys filtering. Values: pending_plan, pending_start, active_outside_dates, none_active, some_active, all_active',
      meta: {
        formula:
          'CASE on media_plans_count, active_buys_outside_plan_count, active_media_plans_count, start_date, end_date',
        inputs: [
          'line_items.media_plans_count',
          'line_items.active_buys_outside_plan_count',
          'line_items.active_media_plans_count',
          'line_items.start_date',
          'line_items.end_date'
        ],
        short_description:
          'Computed status for active platform buys. Values: pending_plan, pending_start, active_outside_dates, none_active, some_active, all_active',
        explanation:
          'Multi-branch CASE: pending_plan if no media plans, active_outside_dates if buys exist outside flight dates, pending_start if not yet started, all_active/some_active/none_active based on active vs total media plans.',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.media_budget_value': {
      sql: 'media_budget',
      type: 'number',
      title: 'Media Budget Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.media_budget',
        short_description: 'Filter-only: per-row media budget',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.estimated_units_value': {
      sql: 'estimated_units',
      type: 'number',
      title: 'Estimated Units Value',
      format: 'number',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.estimated_units',
        short_description: 'Filter-only: per-row estimated units',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.actual_units_value': {
      sql: 'total_actual_units',
      type: 'number',
      title: 'Actual Units Value',
      format: 'number',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.actual_units',
        short_description: 'Filter-only: per-row actual units delivered',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.actual_spend_value': {
      sql: 'total_actual_spend',
      type: 'number',
      title: 'Actual Spend Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.actual_spend',
        short_description: 'Filter-only: per-row actual spend',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.media_cost_value': {
      sql: 'total_media_cost',
      type: 'number',
      title: 'Media Cost Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.media_cost',
        short_description: 'Filter-only: per-row media cost (COGS)',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.uncapped_effective_revenue_value': {
      sql: 'line_items.total_uncapped_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0))',
      type: 'number',
      title: 'Uncapped Effective Revenue Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.uncapped_effective_revenue',
        short_description: 'Filter-only: per-row uncapped effective revenue',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.billable_revenue_value': {
      sql: 'total_billable_revenue',
      type: 'number',
      title: 'Billable Revenue (filter)',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.billable_revenue',
        short_description: 'Filter-only dimension for billable revenue',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.effective_billable_revenue_value': {
      sql: 'line_items.total_billable_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0))',
      type: 'number',
      title: 'Effective Billable Revenue (filter)',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.effective_billable_revenue',
        short_description: 'Filter-only dimension for effective billable revenue',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.target_revenue_value': {
      sql: 'line_items.estimated_units * line_items.effective_unit_price',
      type: 'number',
      title: 'Target Revenue Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.target_revenue',
        short_description: 'Filter-only: per-row target revenue',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.current_planned_units_value': {
      sql: 'current_planned_units',
      type: 'number',
      title: 'Current Planned Units Value',
      format: 'number',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.current_planned_units',
        short_description: 'Filter-only: per-row planned units through yesterday',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.current_planned_spend_value': {
      sql: 'current_planned_spend',
      type: 'number',
      title: 'Current Planned Spend Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.current_planned_spend',
        short_description: 'Filter-only: per-row planned spend through yesterday',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.effective_planned_revenue_value': {
      sql: 'line_items.current_planned_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0))',
      type: 'number',
      title: 'Effective Planned Revenue Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.effective_planned_revenue',
        short_description: 'Filter-only: per-row effective planned revenue',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.monthly_capped_actual_revenue_value': {
      sql: 'total_monthly_capped_actual_revenue',
      type: 'number',
      title: 'Monthly Capped Actual Revenue Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.monthly_capped_actual_revenue',
        short_description: 'Filter-only: per-row monthly capped actual revenue',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.effective_monthly_capped_actual_revenue_value': {
      sql: 'line_items.total_monthly_capped_actual_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0))',
      type: 'number',
      title: 'Effective Monthly Capped Actual Revenue Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.effective_monthly_capped_actual_revenue',
        short_description: 'Filter-only: per-row effective monthly capped revenue',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.lifetime_capped_actual_revenue_value': {
      sql: 'LEAST(line_items.total_uncapped_revenue, line_items.price)',
      type: 'number',
      title: 'Lifetime Capped Actual Revenue Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.lifetime_capped_actual_revenue',
        short_description: 'Filter-only: per-row lifetime capped actual revenue',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.effective_lifetime_capped_actual_revenue_value': {
      sql: 'LEAST(line_items.total_uncapped_revenue, line_items.price) * (1 - COALESCE(line_items.referral_commission_rate, 0))',
      type: 'number',
      title: 'Effective Lifetime Capped Actual Revenue Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.effective_lifetime_capped_actual_revenue',
        short_description: 'Filter-only: per-row effective lifetime capped revenue',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.units_remaining_value': {
      sql: 'GREATEST(0, line_items.estimated_units - line_items.total_actual_units)',
      type: 'number',
      title: 'Units Remaining Value',
      format: 'number',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.units_remaining',
        short_description: 'Filter-only: per-row units remaining',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.budget_remaining_value': {
      sql: 'GREATEST(0, line_items.media_budget - line_items.total_actual_spend)',
      type: 'number',
      title: 'Budget Remaining Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.budget_remaining',
        short_description: 'Filter-only: per-row budget remaining',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.revenue_remaining_value': {
      sql: 'GREATEST(0, line_items.price * (1 - COALESCE(line_items.referral_commission_rate, 0)) - line_items.total_billable_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0)))',
      type: 'number',
      title: 'Revenue Remaining Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.revenue_remaining',
        short_description: 'Filter-only: per-row revenue remaining',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.delivery_pacing_value': {
      sql: 'line_items.total_actual_units / NULLIF(line_items.current_planned_units, 0)',
      type: 'number',
      title: 'Delivery Pacing Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.delivery_pacing',
        short_description: 'Filter-only: per-row delivery pacing',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.spend_pacing_value': {
      sql: 'line_items.total_actual_spend / NULLIF(line_items.current_planned_spend, 0)',
      type: 'number',
      title: 'Spend Pacing Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.spend_pacing',
        short_description: 'Filter-only: per-row spend pacing',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.percent_units_delivered_value': {
      sql: 'line_items.total_actual_units / NULLIF(line_items.estimated_units, 0)',
      type: 'number',
      title: 'Percent Units Delivered Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.percent_units_delivered',
        short_description: 'Filter-only: per-row percent units delivered',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.percent_budget_spent_value': {
      sql: 'line_items.total_actual_spend / NULLIF(line_items.media_budget, 0)',
      type: 'number',
      title: 'Percent Budget Spent Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.percent_budget_spent',
        short_description: 'Filter-only: per-row percent budget spent',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.percent_revenue_delivered_value': {
      sql: '(line_items.total_billable_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0))) / NULLIF(line_items.price * (1 - COALESCE(line_items.referral_commission_rate, 0)), 0)',
      type: 'number',
      title: 'Percent Revenue Delivered Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.percent_revenue_delivered',
        short_description: 'Filter-only: per-row percent revenue delivered',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.margin_actual_value': {
      sql: '(line_items.total_billable_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0)) - line_items.total_media_cost) / NULLIF(line_items.total_billable_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0)), 0)',
      type: 'number',
      title: 'Margin Actual Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.margin_actual',
        short_description: 'Filter-only: per-row actual margin',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.unit_cost_actual_value': {
      sql: 'line_items.total_actual_spend / NULLIF(line_items.total_actual_units, 0)',
      type: 'number',
      title: 'Unit Cost Actual Value',
      format: 'currency',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.unit_cost_actual',
        short_description: 'Filter-only: per-row actual unit cost',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.margin_variance_value': {
      sql: '((line_items.total_billable_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0)) - line_items.total_media_cost) / NULLIF(line_items.total_billable_revenue * (1 - COALESCE(line_items.referral_commission_rate, 0)), 0) - line_items.planned_margin) / NULLIF(line_items.planned_margin, 0)',
      type: 'number',
      title: 'Margin Variance Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.margin_variance',
        short_description: 'Filter-only: per-row margin variance',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.unit_cost_variance_value': {
      sql: '(line_items.total_actual_spend / NULLIF(line_items.total_actual_units, 0) - line_items.planned_unit_cost) / NULLIF(line_items.planned_unit_cost, 0)',
      type: 'number',
      title: 'Unit Cost Variance Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.unit_cost_variance',
        short_description: 'Filter-only: per-row unit cost variance',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry,
    'line_items.target_margin_variance_value': {
      sql: '(line_items.target_margin - line_items.planned_margin) / NULLIF(line_items.target_margin, 0)',
      type: 'number',
      title: 'Target Margin Variance Value',
      format: 'percent',
      meta: {
        filter_only: true,
        measure_counterpart: 'line_items.target_margin_variance',
        short_description: 'Filter-only: per-row target margin variance',
        source_tables: ['metrics.line_items']
      }
    } as const satisfies CubeDimensionEntry
  }
};
